import { useEffect } from 'react';
import { LR_CONFIG_KEY, LR_VERSION, LR_CDN_URL, ALLOWED_COUNTRIES } from './logrocket.constants';
/**
 * Generates a session user ID with country prefix.
 *
 * @param countryCode - ISO-2 country code (e.g. `GB`, `US`)
 * @returns Session ID in format `{COUNTRY}-{hex8}-{hex4}` (e.g. `GB-a3f8c91d-b7e2`)
 */
const createUser = (countryCode) => {
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return `${countryCode}-${s4()}${s4()}-${s4()}`;
};
/**
 * Reads the user's country code from sessionStorage.
 * Checks `geo_ip_location` first (full object with 30-min cache), falls back to `country` (code only).
 *
 * @returns ISO-2 country code or null if not yet available
 */
const getCountryCode = () => {
    try {
        const raw = sessionStorage.getItem('geo_ip_location');
        if (raw)
            return JSON.parse(raw).countryCode || null;
    }
    catch (_a) { }
    return sessionStorage.getItem('country') || null;
};
/**
 * Returns existing LrConfig from sessionStorage or creates a new one via the factory.
 * Persists the result in sessionStorage for reuse across page navigations.
 *
 * @param factory - Creates a new LrConfig when none exists or version changed
 * @returns The existing or newly created config
 */
const getOrCreate = (factory) => {
    try {
        const raw = sessionStorage.getItem(LR_CONFIG_KEY);
        if (raw) {
            const config = JSON.parse(raw);
            if (config.user && config.version === LR_VERSION)
                return config;
        }
    }
    catch (_a) { }
    const config = factory();
    sessionStorage.setItem(LR_CONFIG_KEY, JSON.stringify(config));
    return config;
};
/**
 * Dynamically loads the LogRocket SDK from CDN.
 * No-ops if already loaded or script tag already exists.
 *
 * @returns Promise that resolves when the script is loaded
 */
/**
 * Dynamically loads the LogRocket SDK from CDN.
 * Uses `data-lr-status` attribute on the script tag as a deterministic state check
 * across micro-frontends — avoids race conditions with `load` event listeners.
 *
 * @returns Promise that resolves when the script is loaded
 */
const loadScript = () => {
    return new Promise((resolve, reject) => {
        if (window.LogRocket) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[data-lr]`);
        if (existing) {
            if (existing.dataset.lrStatus === 'loaded') {
                resolve();
                return;
            }
            if (existing.dataset.lrStatus === 'error') {
                reject();
                return;
            }
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject());
            return;
        }
        const script = document.createElement('script');
        script.src = LR_CDN_URL;
        script.crossOrigin = 'anonymous';
        script.defer = true;
        script.dataset.lr = '';
        script.dataset.lrStatus = 'loading';
        script.onload = () => { script.dataset.lrStatus = 'loaded'; resolve(); };
        script.onerror = () => { script.dataset.lrStatus = 'error'; reject(); };
        document.head.appendChild(script);
    });
};
/**
 * Loads LogRocket SDK, initializes it, and identifies the user.
 *
 * @remarks
 * Uses `window.holaLogrocketAppId` set by the theme's `logrocket.liquid` snippet.
 * The user config is created or retrieved via {@link getOrCreate}.
 *
 * @param countryCode - ISO-2 country code for user identification
 */
const initAndIdentify = async (countryCode) => {
    const appId = window.holaLogrocketAppId;
    if (!appId)
        return;
    await loadScript();
    const LR = window.LogRocket;
    if (!LR)
        return;
    if (!window.__lr_initialized) {
        LR.init(appId);
        window.__lr_initialized = true;
    }
    const config = getOrCreate(() => ({
        user: createUser(countryCode),
        key: appId,
        version: LR_VERSION,
        country: countryCode,
    }));
    LR.identify(config.user, { country: config.country, version: config.version });
};
/**
 * React hook that conditionally initializes LogRocket and identifies the user
 * based on their country of origin. Only activates for allowed countries.
 *
 * @remarks
 * - The theme sets `window.holaLogrocketAppId` via `logrocket.liquid`
 * - If the app ID is missing, the hook does nothing
 * - Country is read from `sessionStorage('geo_ip_location')` or
 *   the `theme::current::ip` event if not yet available
 * - Uses `window.__lr_identified` as cross-microfrontend singleton guard
 * - Session config persisted in `sessionStorage('lr_config')` via {@link getOrCreate}
 *
 * @example
 * ```tsx
 * const App: FC = () => {
 *   useLogrocket();
 *   return <SearchBar />;
 * };
 * ```
 */
export const useLogrocket = () => {
    useEffect(() => {
        if (window.__lr_identified)
            return;
        if (!window.holaLogrocketAppId)
            return;
        const tryInit = (code) => {
            if (!code || !ALLOWED_COUNTRIES.has(code.toUpperCase()))
                return;
            window.__lr_identified = true;
            initAndIdentify(code.toUpperCase());
        };
        const country = getCountryCode();
        if (country) {
            tryInit(country);
            return;
        }
        const handler = (event) => {
            const { countryCode } = event.detail.country;
            tryInit(countryCode);
        };
        document.body.addEventListener('theme::current::ip', handler);
        return () => document.body.removeEventListener('theme::current::ip', handler);
    }, []);
};
